import { Renderer2 } from '@angular/core';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Renders and positions a single floating tooltip anchored to a host element.
 *
 * Extracted from TooltipDirective so it can be reused by any directive that
 * needs the same tooltip visual (e.g. the truncate/ellipsis directive) without
 * duplicating the viewport-aware positioning logic.
 */
export class TooltipOverlay {
  private tooltipElement: HTMLElement | null = null;

  constructor(
    private readonly renderer: Renderer2,
    private readonly host: HTMLElement,
  ) {}

  get isVisible(): boolean {
    return this.tooltipElement !== null;
  }

  show(text: string, placement: string | string[] = 'auto'): void {
    if (this.tooltipElement || !text) {
      return;
    }

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.setAttribute(this.tooltipElement, 'role', 'tooltip');

    const classes = [
      'fixed',
      'z-[9999]',
      'px-3',
      'py-1.5',
      'text-xs',
      'font-medium',
      'text-white',
      'bg-slate-900',
      'rounded-md',
      'shadow-md',
      'pointer-events-none',
      'opacity-100',
      'max-w-[250px]',
      'break-words',
      'whitespace-normal',
    ];
    classes.forEach((cls) => this.renderer.addClass(this.tooltipElement!, cls));

    const textNode = this.renderer.createText(text);
    this.renderer.appendChild(this.tooltipElement, textNode);
    this.renderer.appendChild(document.body, this.tooltipElement);

    const resolvedPlacement = this.positionTooltip(placement);

    // Create arrow caret
    const arrowElement = this.renderer.createElement('div');
    const arrowClasses = [
      'absolute',
      'w-1.5',
      'h-1.5',
      'bg-slate-900',
      'rotate-45',
      'pointer-events-none',
    ];
    arrowClasses.forEach((cls) => this.renderer.addClass(arrowElement, cls));

    switch (resolvedPlacement) {
      case 'top':
        this.renderer.addClass(arrowElement, 'bottom-[-3px]');
        this.renderer.addClass(arrowElement, 'left-1/2');
        this.renderer.addClass(arrowElement, '-translate-x-1/2');
        break;
      case 'bottom':
        this.renderer.addClass(arrowElement, 'top-[-3px]');
        this.renderer.addClass(arrowElement, 'left-1/2');
        this.renderer.addClass(arrowElement, '-translate-x-1/2');
        break;
      case 'left':
        this.renderer.addClass(arrowElement, 'right-[-3px]');
        this.renderer.addClass(arrowElement, 'top-1/2');
        this.renderer.addClass(arrowElement, '-translate-y-1/2');
        break;
      case 'right':
        this.renderer.addClass(arrowElement, 'left-[-3px]');
        this.renderer.addClass(arrowElement, 'top-1/2');
        this.renderer.addClass(arrowElement, '-translate-y-1/2');
        break;
    }
    this.renderer.appendChild(this.tooltipElement, arrowElement);
  }

  hide(): void {
    if (!this.tooltipElement) {
      return;
    }

    const element = this.tooltipElement;
    this.tooltipElement = null;

    if (element.parentNode) {
      this.renderer.removeChild(document.body, element);
    }
  }

  private positionTooltip(placement: string | string[]): TooltipPlacement {
    if (!this.tooltipElement) {
      return 'top';
    }

    const hostRect = this.host.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const offset = 8;

    const rawPlacements = Array.isArray(placement)
      ? placement
      : typeof placement === 'string'
        ? placement.split(/[\s,]+/)
        : ['top'];

    const resolvedPlacements: string[] = [];
    const hasAuto = rawPlacements.includes('auto');

    rawPlacements.forEach((p) => {
      const clean = p.trim().toLowerCase();
      if (clean && clean !== 'auto' && ['top', 'bottom', 'left', 'right'].includes(clean)) {
        resolvedPlacements.push(clean);
      }
    });

    if (hasAuto || resolvedPlacements.length === 0) {
      const fallbacks = ['top', 'bottom', 'left', 'right'];
      fallbacks.forEach((f) => {
        if (!resolvedPlacements.includes(f)) {
          resolvedPlacements.push(f);
        }
      });
    }

    let chosenPlacement = resolvedPlacements[0] as TooltipPlacement;
    for (const p of resolvedPlacements) {
      if (this.fits(p, hostRect, tooltipRect, viewportWidth, viewportHeight, offset)) {
        chosenPlacement = p as TooltipPlacement;
        break;
      }
    }

    let top = 0;
    let left = 0;

    switch (chosenPlacement) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + offset;
        break;
    }

    if (left < offset) {
      left = offset;
    } else if (left + tooltipRect.width > viewportWidth - offset) {
      left = viewportWidth - tooltipRect.width - offset;
    }

    if (top < offset) {
      top = offset;
    } else if (top + tooltipRect.height > viewportHeight - offset) {
      top = viewportHeight - tooltipRect.height - offset;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left + window.scrollX}px`);

    return chosenPlacement;
  }

  private fits(
    placement: string,
    hostRect: DOMRect,
    tooltipRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    offset: number,
  ): boolean {
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + offset;
        break;
      default:
        return false;
    }

    return (
      top >= offset &&
      left >= offset &&
      top + tooltipRect.height <= viewportHeight - offset &&
      left + tooltipRect.width <= viewportWidth - offset
    );
  }
}
